import {
  GraphQLInputObjectType,
  GraphQLString,
  GraphQLNonNull,
  GraphQLID,
  GraphQLBoolean,
  GraphQLInt,
  GraphQLList
} from 'graphql'
import {
  StacktraceCaptureMode,
  ServerSamplingFields,
  serverEnvironmentType,
  stacktraceCaptureModeType,
  logLevelType,
  LogLevel
} from './server'
import {
  SyslogFields,
  syslogSeverityType,
  syslogProtocolType
} from './syslog-config'
import { ServerFilterArgType, ServerFilterArg } from './server-filter-arg'
import { DiagnosticCollectionFormValue } from '../../../components/server-settings'

export interface ServerSettingsMutationArgs {
  readonly input?: {
    readonly organizationID: string
    readonly id: string
    readonly settings: SingleServerSettings
  }
}

export interface DiagnosticCollectionMutationArgs {
  readonly input?: {
    readonly organizationID: string
    readonly id: string
    readonly diagnosticCollection: DiagnosticCollectionFormValue
  }
}

export interface BulkServerSettingsMutationArgs {
  readonly input?: {
    readonly organizationID: string
    readonly ids: string[]
    readonly settings: BulkServerSettings
  }
}

export interface BulkServerSettingsFilterMutationArgs {
  readonly input?: {
    readonly organizationID: string
    readonly filters: ServerFilterArg
    readonly settings: BulkServerSettings
  }
}

interface AssessSettings {
  readonly stacktraceCaptureMode: StacktraceCaptureMode
  readonly samplingProfile?: string
  readonly sampling?: ServerSamplingFields
}

interface ProtectSettings {
  readonly botProtection: boolean
  readonly syslog?: SyslogFields
}

export interface SingleServerSettings {
  readonly name: string
  readonly logPath?: string
  readonly environment: string
  readonly logLevel: LogLevel
  readonly assess?: AssessSettings
  readonly protect?: ProtectSettings
}

export interface BulkServerSettings {
  readonly environment?: string
  readonly logLevel?: LogLevel
  assess?: Partial<AssessSettings>
  readonly protect?: Partial<ProtectSettings>
}

const samplingFieldsArgsType = new GraphQLInputObjectType({
  name: 'SamplingFieldsArgs',
  description: 'Configuration for assess result sampling',
  fields: () => ({
    baseline: {
      name: 'The number of times to analyze results prior to sampling',
      type: new GraphQLNonNull(GraphQLInt)
    },
    frequency: {
      name: 'Analyze results one time out of this number of requests (sample size)',
      type: new GraphQLNonNull(GraphQLInt)
    },
    window: {
      name: 'How many seconds to retain samples',
      type: new GraphQLNonNull(GraphQLInt)
    }
  })
})

const syslogFieldsArgsType = new GraphQLInputObjectType({
  name: 'SyslogFieldsArgs',
  description: 'Configuration for output to syslog',
  fields: () => ({
    ipAddress: {
      description: 'The address of the Syslog server',
      type: new GraphQLNonNull(GraphQLString)
    },
    port: {
      description: 'The port of the Syslog server',
      type: new GraphQLNonNull(GraphQLInt)
    },
    facilityCode: {
      description: 'Syslog facility code',
      type: new GraphQLNonNull(GraphQLInt)
    },
    protocol: {
      description: 'Syslog Protocol',
      type: new GraphQLNonNull(syslogProtocolType)
    },
    severityExploited: {
      description: 'Syslog severity level for exploited events',
      type: new GraphQLNonNull(syslogSeverityType)
    },
    severitySuspicious: {
      description: 'Syslog severity level for suspicious events',
      type: new GraphQLNonNull(syslogSeverityType)
    },
    severityBlocked: {
      description: 'Syslog severity level for blocked events',
      type: new GraphQLNonNull(syslogSeverityType)
    },
    severityBlockedPerimeter: {
      description: 'Syslog severity level for blocked at perimeter events',
      type: new GraphQLNonNull(syslogSeverityType)
    },
    severityProbed: {
      description: 'Syslog severity level for probed events',
      type: new GraphQLNonNull(syslogSeverityType)
    }
  })
})

const sampling = {
  description:
    'Settings for sampling. No value implies sampling is off. Only valid if the server has assess enabled.',
  type: samplingFieldsArgsType
}

const syslog = {
  description:
    'Settings for output to Syslog. No value implies sampling is off. Only valid if the server has protect enabled.',
  type: syslogFieldsArgsType
}

export const serverSettingsMutationArgs = {
  input: {
    type: new GraphQLInputObjectType({
      name: 'ServerSettingsMutationArg',
      description:
        'Parameters that describe how to update the settings of a server',
      fields: () => ({
        organizationID: {
          description: 'The ID of the organization of the server being updated',
          type: new GraphQLNonNull(GraphQLID)
        },
        id: {
          description: 'ID of the server being updated.',
          type: new GraphQLNonNull(GraphQLID)
        },
        settings: {
          description: 'The new settings for the specified server',
          type: new GraphQLNonNull(
            new GraphQLInputObjectType({
              name: 'SingleServerSettingsArg',
              fields: () => ({
                name: {
                  description: 'The human-readable name of the server',
                  type: new GraphQLNonNull(GraphQLString)
                },
                logPath: {
                  description: 'The logging file path',
                  type: GraphQLString
                },
                environment: {
                  description: 'The deployment environment',
                  type: new GraphQLNonNull(serverEnvironmentType)
                },
                logLevel: {
                  description: 'The logging level',
                  type: new GraphQLNonNull(logLevelType)
                },
                assess: {
                  description:
                    'Settings related to assess mode. No value implies assess is off.',
                  type: new GraphQLInputObjectType({
                    name: 'BulkAssessSettings',
                    fields: () => ({
                      stacktraceCaptureMode: {
                        description:
                          'The capture mode for stack traces. Only valid if the server has assess enabled.',
                        type: new GraphQLNonNull(stacktraceCaptureModeType)
                      },
                      samplingProfile: {
                        description: 'sampling profile.',
                        type: GraphQLString
                      },
                      sampling
                    })
                  })
                },
                protect: {
                  description:
                    'Settings related to protect mode. No value implies protect is off.',
                  type: new GraphQLInputObjectType({
                    name: 'BulkProtectSettings',
                    fields: () => ({
                      botProtection: {
                        description:
                          'Whether or not the server should block requests from known bots. Only valid if the server has protect enabled.',
                        type: new GraphQLNonNull(GraphQLBoolean)
                      },
                      syslog
                    })
                  })
                }
              })
            })
          )
        }
      })
    })
  }
}

export const diagnosticCollectionMutationArgs = {
  input: {
    type: new GraphQLInputObjectType({
      name: 'DiagnosticCollectionSettingsMutationArg',
      description:
        'Parameters that describe how to update the diagnostic collection settings of a server',
      fields: () => ({
        organizationID: {
          description: 'The ID of the organization of the server being updated',
          type: new GraphQLNonNull(GraphQLID)
        },
        id: {
          description: 'ID of the server being updated.',
          type: new GraphQLNonNull(GraphQLID)
        },
        diagnosticCollection: {
          description: 'The new settings for the specified server',
          type: new GraphQLNonNull(
            new GraphQLInputObjectType({
              name: 'DiagnosticCollectionSettingsArg',
              fields: () => ({
                enabled: {
                  description: 'Is diagnostic collection enabled',
                  type: new GraphQLNonNull(GraphQLBoolean)
                },
                logLevel: {
                  description: 'The logging level to collect',
                  type: new GraphQLNonNull(logLevelType)
                },
                duration: {
                  description: 'The diagnostic collection duration time',
                  type: new GraphQLNonNull(GraphQLInt)
                }
              })
            })
          )
        }
      })
    })
  }
}

const bulkServerSettingsArg = {
  description: 'The new settings for the specified server',
  type: new GraphQLNonNull(
    new GraphQLInputObjectType({
      name: 'BulkServerSettingsArg',
      fields: () => ({
        environment: {
          description: 'The deployment environment',
          type: serverEnvironmentType
        },
        logLevel: {
          description: 'The logging level',
          type: logLevelType
        },
        assess: {
          description:
            'Settings related to assess mode. No value implies assess is off.',
          type: new GraphQLInputObjectType({
            name: 'AssessSettings',
            fields: () => ({
              samplingProfile: {
                description: 'sampling profile.',
                type: GraphQLString
              },
              stacktraceCaptureMode: {
                description:
                  'The capture mode for stack traces. Only valid if the server has assess enabled.',
                type: stacktraceCaptureModeType
              },
              sampling
            })
          })
        },
        protect: {
          description:
            'Settings related to protect mode. No value implies protect is off.',
          type: new GraphQLInputObjectType({
            name: 'ProtectSettings',
            fields: () => ({
              botProtection: {
                description:
                  'Whether or not the server should block requests from known bots. Only valid if the server has protect enabled.',
                type: GraphQLBoolean
              },
              syslog
            })
          })
        }
      })
    })
  )
}

export const bulkServerSettingsMutationArg = {
  input: {
    type: new GraphQLInputObjectType({
      name: 'BulkServerSettingsMutationArg',
      description:
        'Parameters that describe how to update the settings of a set of servers',
      fields: () => ({
        organizationID: {
          description:
            'The ID of the organization of the servers being updated',
          type: new GraphQLNonNull(GraphQLID)
        },
        ids: {
          description: 'List of IDs of the servers being updated.',
          type: new GraphQLNonNull(
            new GraphQLList(new GraphQLNonNull(GraphQLID))
          )
        },
        settings: bulkServerSettingsArg
      })
    })
  }
}

export const bulkServerSettingsFilterMutationArg = {
  input: {
    type: new GraphQLInputObjectType({
      name: 'BulkServerSettingsFilterMutationArg',
      description:
        'Parameters that describe how to update the settings of a selection of servers based on filters',
      fields: () => ({
        organizationID: {
          description:
            'The ID of the organization of the servers being updated',
          type: new GraphQLNonNull(GraphQLID)
        },
        filters: {
          description:
            'A collection of filters to specify which servers to update',
          type: new GraphQLNonNull(ServerFilterArgType)
        },
        settings: bulkServerSettingsArg
      })
    })
  }
}
